import { useContext, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/system';
import { CustomCompactContainer, LedgerTable } from 'components';
import CellAnnotationValueViewer from 'components/CellAnnotationValueViewer';
import { buildCellKey } from 'components/CellAnnotationValueViewer/types';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import FundOwnershipContext from 'context/FundOwnershipContext';
import { NET_SHARES_CROSS_REFERENCE_CONFIG } from 'pages/CapTable/cell-annotations';
import { AMOUNT_ALIAS, COST_BASIS_ALIAS } from 'pages/CapTable/fund-ownership/components/shares-ledger/constants';
import useExchangeRateMap from 'pages/CapTable/fund-ownership/components/shares-ledger/hooks/useExchangeRateMap';
import { shortDate } from 'utilities/dateFormats';
import { colConfig } from './colConfig';
import { LedgerCell, LedgerCellSubType, OutstandingProps, OutstandingRow } from './types';
import calculateOutstandingFromCells from '../../utilities/calculateRegularOutstandingFromCells';

// Django ContentType label for FundOwnershipDetail. The cell-annotation
// cellKey uses this label; mismatching it breaks store addressing and
// backend parent resolution.
const FOD_CONTENT_TYPE = 'fundownershipdetail';
const NET_SHARES_FIELD_ALIAS = 'net_shares';

const Outstanding = ({
  outstandingShares,
  outstandingCostBasis,
  outstandingAmount,
  doFullValidation,
  setIsValid,
  updateValidationStatus,
  isDisabled,
  modeledAfterConvertibleNote,
  sourceSecurity,
  fundCurrency,
  isDifferentCurrency,
  selectedMeasurementDate,
  format,
  outstandingLoanValue,
  isLoanNote,
  purchasedCells,
  soldCells,
  outstandingValuesForDividends,
  sharesMaxDecimalPlaces,
  fundOwnershipDetailId,
}: OutstandingProps) => {
  const netSharesAnnotationCellKey = useMemo(
    () =>
      fundOwnershipDetailId != null
        ? buildCellKey(FOD_CONTENT_TYPE, fundOwnershipDetailId, NET_SHARES_FIELD_ALIAS, 'cross_reference')
        : undefined,
    [fundOwnershipDetailId]
  );
  const security = sourceSecurity;
  const [rows, setRows] = useState<OutstandingRow[]>([]);
  const [ledgerCells, setLedgerCells] = useState<LedgerCell>();
  const { cells } = useContext(FeaturedSpreadsheetContext);
  const { measurementDate } = useContext(FundOwnershipContext);

  const { exchangeRateMap } = useExchangeRateMap({
    fundCurrency,
    format,
    shares: rows,
    selectedMeasurementDate,
    isDifferentCurrency,
    cellKeys: [COST_BASIS_ALIAS, AMOUNT_ALIAS],
  });
  // outstandingDate should be the measurementDate
  const outstandingDate = useMemo(() => (measurementDate ? shortDate(measurementDate) : ''), [measurementDate]);

  const config = useMemo(
    () => colConfig(sharesMaxDecimalPlaces, modeledAfterConvertibleNote, !!security?.has_dividends, isLoanNote),
    [isLoanNote, modeledAfterConvertibleNote, security?.has_dividends, sharesMaxDecimalPlaces]
  );

  // Parse cells
  useEffect(() => {
    if (modeledAfterConvertibleNote) {
      const amount = outstandingAmount ?? 0;

      // Define an object for the row data
      const rowData: OutstandingRow = {
        date: outstandingDate ?? '',
        amount,
      };

      // Define an object for the ledger cells data
      const ledgerCellsData: LedgerCell = {
        date1: { value: outstandingDate ?? '', readOnly: true },
        amount1: { value: amount, readOnly: true, alias: AMOUNT_ALIAS }
      };

      // Include loan_value if loanNote is truthy
      if (isLoanNote) {
        rowData.loan_value = outstandingLoanValue;
        ledgerCellsData.loan_value1 = { value: outstandingLoanValue, readOnly: true, alias: 'loan_value' };
      }
      setRows([rowData]);
      setLedgerCells(ledgerCellsData);
    } else {
      const { sharesOutstanding, costBasisOutstanding } = calculateOutstandingFromCells({ purchasedCells, soldCells });

      setRows([
        {
          date: outstandingDate ?? '',
          shares: sharesOutstanding || 0,
          cost_basis: costBasisOutstanding || 0,
        },
      ]);

      const cellData = {
        value: costBasisOutstanding || 0,
        readOnly: true,
        alias: COST_BASIS_ALIAS,
      };

      const costBasis1 = cellData;
      const { cumulative_dividends, total_shares_outstanding } = outstandingValuesForDividends;

      const sharesCell: LedgerCellSubType = netSharesAnnotationCellKey
        ? {
          value: sharesOutstanding || 0,
          readOnly: true,
          valueViewer: CellAnnotationValueViewer,
          cellAnnotation: NET_SHARES_CROSS_REFERENCE_CONFIG,
          cellAnnotationCellKey: netSharesAnnotationCellKey,
        }
        : { value: sharesOutstanding || 0, readOnly: true };

      setLedgerCells({
        date1: { value: outstandingDate, readOnly: true, alias: 'date' },
        shares1: sharesCell,
        cost_basis1: costBasis1,
        cumulative_dividends1: {
          value: cumulative_dividends,
          readOnly: true,
        },
        total_shares_outstanding1: {
          value: total_shares_outstanding,
          readOnly: true,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    purchasedCells,
    soldCells,
    outstandingAmount,
    outstandingShares,
    outstandingCostBasis,
    cells,
    security,
    measurementDate,
    outstandingDate,
    outstandingValuesForDividends,
    netSharesAnnotationCellKey,
  ]);

  return (
    <CustomCompactContainer direction="column" rowSpacing={2.5} wrap="nowrap">
      <Grid>
        <h4>Outstanding</h4>
      </Grid>

      <Grid>
        <LedgerTable
          colConfig={config}
          rows={rows}
          showAddRowButton={false}
          showDeleteColumn={false}
          disabled={isDisabled}
          tableTerms={{
            tableName: 'Outstanding',
            tableSlug: 'outstanding',
            columnName: 'Outstanding',
            pluralColumnName: 'Outstandings',
          }}
          cells={ledgerCells}
          setCells={setLedgerCells}
          doFullValidation={doFullValidation}
          setIsValid={setIsValid}
          updateValidationStatus={updateValidationStatus}
          currency={format.currency}
          exchangeRateMap={exchangeRateMap}
        />
      </Grid>
    </CustomCompactContainer>
  );
};

export default Outstanding;
